import { PLAYLIST, ARTIST } from "@/data"
import type { TrackData, State } from "./playerTypes"

/* =========================
   STORAGE
========================= */

export const getStoredTrackData = (): TrackData | null => {
  try {
    const data = JSON.parse(
      localStorage.getItem("lastTrack") || "null"
    )

    return data && data.track ? (data as TrackData) : null
  } catch (error) {
    console.error("Error al obtener datos del localStorage:", error)
    return null
  }
}

export const saveTrackToStorage = (trackData: TrackData) => {
  localStorage.setItem("lastTrack", JSON.stringify(trackData))
}

/* =========================
   HELPERS
========================= */

export const isValidSong = (song: any) =>
  song && song.link?.trim() && song.songimg?.trim()

export const hasPlaylistData = (playlist: any): playlist is { playlistData: any[] } =>
  playlist && Array.isArray(playlist.playlistData)

/* =========================
   CORE
========================= */

export const getTrackData = (
  playlist: any,
  trackIndex: number
): TrackData => {
  const song = playlist.playlistData[trackIndex]

  const {
    link,
    songName,
    songimg,
    songArtist,
    trackTime,
  } = song

  return {
    trackKey: [playlist.id, trackIndex],
    track: link,
    trackName: songName,
    trackImg: songimg,
    trackArtist: songArtist,
    playlistTitle: playlist.title,
    playlistLink: playlist.link,
    trackTime: trackTime || "",
    type: playlist.type,
    trackTimeElapsed: 0,
  }
}

/* =========================
   RANDOM TRACK
========================= */

export const getRandomTrack = (): TrackData | null => {
  const validPlaylists = PLAYLIST
    .filter(hasPlaylistData)
    .map((p: any) => ({
      ...p,
      playlistData: p.playlistData.filter(isValidSong),
    }))
    .filter((p: any) => p.playlistData.length > 0)

  if (!validPlaylists.length) {
    console.error("No hay canciones válidas disponibles.")
    return null
  }

  const playlistIndex = Math.floor(Math.random() * validPlaylists.length)
  const playlist = validPlaylists[playlistIndex]

  const trackIndex = Math.floor(
    Math.random() * playlist.playlistData.length
  )

  return getTrackData(playlist, trackIndex)
}

/* =========================
   OFFLINE TRACK
========================= */

export const getOfflineTrackData = (payload: any): TrackData | null => {
  const { trackKey, trackData } = payload

  if (!trackData || !trackKey) {
    console.error("Payload offline incompleto")
    return null
  }

  return {
    trackKey,
    track: trackData.songUrl,
    trackName: trackData.songName,
    trackImg: trackData.songimg,
    trackArtist: trackData.songArtist,
    playlistTitle: "Offline",
    playlistLink: "",
    trackTime: trackData.trackTime || "",
    type: "offline",
    trackTimeElapsed: 0,
  }
}

/* =========================
   CHANGE TRACK
========================= */

export const changeTrack = (
  state: State,
  payload: any
): State => {
  /* OFFLINE MODE */
  if (
    payload &&
    typeof payload === "object" &&
    Array.isArray(payload.trackKey) &&
    payload.trackKey[0] === -1
  ) {
    const offlineTrack = getOfflineTrackData(payload)
    if (!offlineTrack) return state

    saveTrackToStorage(offlineTrack)

    return {
      ...state,
      trackData: offlineTrack,
      isPlaying: true,
    }
  }

  /* VALIDATION */
  if (!Array.isArray(payload) || payload.length !== 2) {
    console.error("Payload inválido:", payload)
    return state
  }

  const [sourceId, trackIndex] =
    payload as [string | number, number]
  
  /* =========================
     VIRTUAL PLAYLISTS
  ========================= */
  
  if (
    sourceId === "favorites" ||
    sourceId === "offline"
  ) {
    const currentTrack =
      state.trackData
  
    /*
     * Para playlists virtuales,
     * playSong() ya ha establecido
     * los datos completos de la canción.
     *
     * Solo actualizamos la posición.
     */
  
    if (!currentTrack?.track) {
      console.error(
        "No hay trackData disponible para playlist virtual:",
        sourceId
      )
    
      return state
    }
  
    const virtualTrackData: TrackData = {
      ...currentTrack,
    
      trackKey: [
        sourceId,
        trackIndex,
      ],
    
      type: sourceId,
    
      playlistTitle:
        sourceId === "favorites"
          ? "Mis Favoritos"
          : "Offline",
    
      playlistLink:
        sourceId,
    
      trackTimeElapsed: 0,
    }
  
    saveTrackToStorage(
      virtualTrackData
    )
  
    return {
      ...state,
    
      trackData:
        virtualTrackData,
    
      isPlaying: true,
    }
  }
  
  /* =========================
     NORMAL PLAYLISTS
  ========================= */
  
  const playlist =
    PLAYLIST.find(
      (item: any) =>
        String(item.id) === String(sourceId) &&
        hasPlaylistData(item)
    ) ??
    ARTIST.find(
      (item: any) =>
        String(item.id) === String(sourceId) &&
        hasPlaylistData(item)
    )
  
  if (!playlist) {
    console.error(
      "No encontrado en PLAYLIST ni ARTIST:",
      sourceId
    )
  
    return state
  }



  //const playlistData = (playlist as any).playlistData
  const playlistData = (playlist as any).playlistData

  if (!Array.isArray(playlistData)) {
    console.error("playlistData inválido")
    return state
  }

  if (trackIndex < 0 || trackIndex >= playlistData.length) {
    console.error("Índice fuera de rango.")
    return state
  }

  const song = playlistData[trackIndex]

  if (!song || !song.link || !song.songimg) {
    console.error("Canción inválida.")
    return state
  }

  const newTrackData = getTrackData(
    { ...playlist, playlistData },
    trackIndex
  )

  saveTrackToStorage(newTrackData)

  const isSameTrack =
    state.trackData.trackKey?.[0] === newTrackData.trackKey[0] &&
    state.trackData.trackKey?.[1] === newTrackData.trackKey[1]

  return {
    ...state,
    trackData: newTrackData,
    isPlaying: isSameTrack ? state.isPlaying : true,
  }
}